import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { getPlans, createPlan, deletePlan, updatePlan, getGyms, importPlans } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, Calendar, Clock, Building2, ChevronDown, ChevronRight, Pencil, DollarSign, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPlans() {
  const { admin, isSuperAdmin } = useAuth();
  const { labels } = useBusiness();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlan, setNewPlan] = useState({
    name: '', description: '', price: '', duration_days: '', access_type: 'unlimited', gym_id: '', is_staff_only: false, category: 'General'
  });
  const [gyms, setGyms] = useState([]);
  const [collapsedGyms, setCollapsedGyms] = useState({});
  const [editPlan, setEditPlan] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importGymId, setImportGymId] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPlans();
    if (isSuperAdmin) fetchGyms();
  }, []);

  const fetchGyms = async () => {
    try {
      const response = await getGyms();
      setGyms(response.data);
    } catch (error) {
      console.error('Error fetching gyms:', error);
    }
  };

  const fetchPlans = async () => {
    try {
      const gymId = isSuperAdmin ? null : admin?.gym_id;
      const response = await getPlans(gymId);
      setPlans(response.data);
    } catch (error) {
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.name || !newPlan.price || !newPlan.duration_days) {
      toast.error('Nombre, precio y duracion son requeridos');
      return;
    }
    const gymId = isSuperAdmin ? newPlan.gym_id : admin?.gym_id;
    if (!gymId) { toast.error('Selecciona un gimnasio'); return; }
    try {
      if (editPlan) {
        await updatePlan(editPlan.id, { ...newPlan, price: parseFloat(newPlan.price), duration_days: parseInt(newPlan.duration_days), is_staff_only: !!newPlan.is_staff_only, category: (newPlan.category || 'General').trim() || 'General' });
        toast.success('Plan actualizado');
      } else {
        await createPlan({ ...newPlan, gym_id: gymId, price: parseFloat(newPlan.price), duration_days: parseInt(newPlan.duration_days), is_staff_only: !!newPlan.is_staff_only, category: (newPlan.category || 'General').trim() || 'General' });
        toast.success('Plan creado exitosamente');
      }
      setShowCreateModal(false);
      setEditPlan(null);
      setNewPlan({ name: '', description: '', price: '', duration_days: '', access_type: 'unlimited', gym_id: '', is_staff_only: false, category: 'General' });
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openEditPlan = (plan) => {
    setEditPlan(plan);
    setNewPlan({
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price?.toString() || '',
      duration_days: plan.duration_days?.toString() || '',
      access_type: plan.access_type || 'unlimited',
      gym_id: plan.gym_id || '',
      is_staff_only: !!plan.is_staff_only,
      category: plan.category || 'General'
    });
    setShowCreateModal(true);
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Eliminar este plan?')) return;
    try { await deletePlan(planId); toast.success('Plan eliminado'); fetchPlans(); }
    catch (error) { toast.error('Error al eliminar plan'); }
  };

  const getDurationLabel = (days) => {
    if (days === 1) return '1 dia';
    if (days === 7) return '1 semana';
    if (days === 30) return '1 mes';
    if (days === 90) return '3 meses';
    if (days === 180) return '6 meses';
    if (days === 365) return '1 ano';
    return `${days} dias`;
  };

  const toggleGym = (gymId) => setCollapsedGyms(prev => ({ ...prev, [gymId]: !prev[gymId] }));

  // Group plans by gym
  const plansByGym = {};
  plans.forEach(plan => {
    const gid = plan.gym_id || 'unknown';
    if (!plansByGym[gid]) plansByGym[gid] = [];
    plansByGym[gid].push(plan);
  });

  const getGymName = (gymId) => {
    const gym = gyms.find(g => g.id === gymId);
    return gym?.name || gymId;
  };

  return (
    <div className="space-y-6" data-testid="admin-plans">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black tracking-tight">{labels.plans} de {labels.membership}</h1>
          <p style={{ color: 'var(--text-secondary)' }} className="text-sm">{plans.length} {labels.plans.toLowerCase()} en {Object.keys(plansByGym).length} negocio(s)</p>
        </div>
        <div className="flex gap-2">
        {isSuperAdmin && (
          <Button variant="outline" className="border-emerald-700 text-emerald-400 hover:bg-emerald-900/30"
            onClick={() => { setShowImportModal(true); setImportResult(null); setImportFile(null); setImportPreview(null); }}
            data-testid="import-plans-btn">
            <Upload size={16} className="mr-2" /> Importar Tarifas
          </Button>
        )}
        <Dialog open={showCreateModal} onOpenChange={(v) => { setShowCreateModal(v); if (!v) setEditPlan(null); }}>
          <DialogTrigger asChild>
            <Button className="btn-gym-primary" data-testid="create-plan-btn" onClick={() => { setEditPlan(null); setNewPlan({ name: '', description: '', price: '', duration_days: '', access_type: 'unlimited', gym_id: '', is_staff_only: false, category: 'General' }); }}>
              <Plus size={20} className="mr-2" /> Nuevo Plan
            </Button>
          </DialogTrigger>
          <DialogContent style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}>
            <DialogHeader>
              <DialogTitle>{editPlan ? 'Editar Plan' : 'Crear Nuevo Plan'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {isSuperAdmin && (
                <div>
                  <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Gimnasio *</label>
                  <Select value={newPlan.gym_id || "none"} onValueChange={(v) => setNewPlan({ ...newPlan, gym_id: v === "none" ? "" : v })}>
                    <SelectTrigger style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-secondary)' }} data-testid="plan-gym-select">
                      <SelectValue placeholder="Seleccionar gimnasio" />
                    </SelectTrigger>
                    <SelectContent style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-secondary)' }}>
                      <SelectItem value="none">Seleccionar...</SelectItem>
                      {gyms.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Nombre del Plan</label>
                <Input value={newPlan.name} onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })} placeholder="Ej: Plan Mensual" className="input-dark" data-testid="plan-name-input" />
              </div>
              <div>
                <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Descripcion (opcional)</label>
                <Input value={newPlan.description} onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })} placeholder="Acceso ilimitado al gimnasio" className="input-dark" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Precio</label>
                  <Input type="number" step="0.01" value={newPlan.price} onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })} placeholder="30.00" className="input-dark" data-testid="plan-price-input" />
                </div>
                <div>
                  <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Duracion (dias)</label>
                  <Input type="number" value={newPlan.duration_days} onChange={(e) => setNewPlan({ ...newPlan, duration_days: e.target.value })} placeholder="30" className="input-dark" data-testid="plan-duration-input" />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[{ days: 1, label: '1 dia' }, { days: 7, label: '1 semana' }, { days: 30, label: '1 mes' }, { days: 90, label: '3 meses' }, { days: 365, label: '1 ano' }].map((preset) => (
                  <button key={preset.days} type="button" onClick={() => setNewPlan({ ...newPlan, duration_days: preset.days.toString() })}
                    className="px-3 py-1 rounded-full text-xs border transition-colors"
                    style={newPlan.duration_days === preset.days.toString()
                      ? { borderColor: 'var(--gym-primary)', color: 'var(--gym-primary)' }
                      : { borderColor: 'var(--border-secondary)', color: 'var(--text-secondary)' }
                    }>{preset.label}</button>
                ))}
              </div>
              <div>
                <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Actividad / Categoria</label>
                <Input
                  value={newPlan.category || ''}
                  onChange={(e) => setNewPlan({ ...newPlan, category: e.target.value })}
                  placeholder="Ej: Kickboxing, Boxeo, Capoeira, General..."
                  className="input-dark"
                  list="plan-categories-list"
                  data-testid="plan-category-input"
                />
                <datalist id="plan-categories-list">
                  {Array.from(new Set((plans || []).map(p => p.category).filter(Boolean))).map(c => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  Agrupa planes por disciplina para facturacion separada (deja vacio para "General").
                </p>
              </div>
              <label className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors hover:bg-zinc-800/40"
                style={{
                  background: newPlan.is_staff_only ? 'rgba(245, 158, 11, 0.08)' : 'var(--bg-tertiary)',
                  borderColor: newPlan.is_staff_only ? 'rgb(245, 158, 11)' : 'var(--border-secondary)'
                }}
                data-testid="plan-staff-only-toggle">
                <input
                  type="checkbox"
                  checked={!!newPlan.is_staff_only}
                  onChange={(e) => setNewPlan({ ...newPlan, is_staff_only: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-amber-500"
                  data-testid="plan-staff-only-checkbox"
                />
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: newPlan.is_staff_only ? 'rgb(245, 158, 11)' : 'var(--text-primary)' }}>
                    Plan interno (trabajadores / staff)
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    Oculto al publico en la PWA y excluido del ranking de gamificacion.
                  </p>
                </div>
              </label>
              <Button onClick={handleCreatePlan} className="w-full btn-gym-primary" data-testid="save-plan-btn">
                {editPlan ? <><Pencil size={20} className="mr-2" /> Guardar Cambios</> : <><Plus size={20} className="mr-2" /> Crear Plan</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <div key={i} className="stat-card"><div className="skeleton h-6 w-32 mb-4" /><div className="skeleton h-10 w-24 mb-4" /><div className="skeleton h-4 w-full" /></div>)}
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12">
          <Calendar size={48} className="mx-auto mb-4" style={{ color: 'var(--text-dim)' }} />
          <p style={{ color: 'var(--text-muted)' }}>No hay planes creados</p>
          <p style={{ color: 'var(--text-dim)' }} className="text-sm">Crea tu primer plan de membresia</p>
        </div>
      ) : isSuperAdmin ? (
        // SUPER ADMIN: grouped by gym
        <div className="space-y-4">
          {Object.entries(plansByGym).map(([gymId, gymPlans]) => (
            <div key={gymId} className="stat-card" data-testid={`gym-plans-${gymId}`}>
              <button onClick={() => toggleGym(gymId)} className="w-full flex items-center gap-3 mb-4 text-left" data-testid={`toggle-gym-${gymId}`}>
                <Building2 size={20} style={{ color: 'var(--gym-primary)' }} />
                <h2 className="text-lg font-bold flex-1">{getGymName(gymId)}</h2>
                <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>{gymPlans.length} plan(es)</span>
                {collapsedGyms[gymId] ? <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} /> : <ChevronDown size={18} style={{ color: 'var(--text-muted)' }} />}
              </button>
              {!collapsedGyms[gymId] && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {gymPlans.map((plan) => (
                    <div key={plan.id} className="relative group p-4 rounded-xl border transition-colors" style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-primary)' }}>
                      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEditPlan(plan)} className="p-1.5 rounded-lg hover:text-blue-400" style={{ background: 'var(--bg-secondary)' }} data-testid={`edit-plan-${plan.id}`}>
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => handleDeletePlan(plan.id)} className="p-1.5 rounded-lg hover:text-red-500" style={{ background: 'var(--bg-secondary)' }} data-testid={`delete-plan-${plan.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <h3 className="font-bold mb-2 flex items-center gap-2">
                        {plan.name}
                        {plan.is_staff_only && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid={`staff-badge-${plan.id}`}>
                            INTERNO
                          </span>
                        )}
                      </h3>
                      <div className="flex items-baseline gap-1 mb-3">
                        <span className="text-3xl font-black" style={{ color: 'var(--gym-primary)' }}>{formatCurrency(plan.price)}</span>
                      </div>
                      <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <div className="flex items-center gap-2"><Clock size={14} /><span>{getDurationLabel(plan.duration_days)}</span></div>
                        {plan.description && <p className="pt-2 border-t" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>{plan.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        // GYM ADMIN: flat grid
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan.id} className="stat-card relative group">
              <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => openEditPlan(plan)} className="p-2 rounded-lg hover:text-blue-400" style={{ background: 'var(--bg-tertiary)' }} data-testid={`edit-plan-${plan.id}`}>
                  <Pencil size={16} />
                </button>
                <button onClick={() => handleDeletePlan(plan.id)} className="p-2 rounded-lg hover:text-red-500" style={{ background: 'var(--bg-tertiary)' }} data-testid={`delete-plan-${plan.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
              <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                {plan.name}
                {plan.is_staff_only && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40" data-testid={`staff-badge-${plan.id}`}>
                    INTERNO
                  </span>
                )}
              </h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-4xl font-black" style={{ color: 'var(--gym-primary)' }}>{formatCurrency(plan.price)}</span>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><Clock size={16} /><span>{getDurationLabel(plan.duration_days)}</span></div>
                <div className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}><DollarSign size={16} /><span>{formatCurrency((plan.price || 0) / (plan.duration_days || 1))}/dia</span></div>
                {plan.description && <p className="pt-2 border-t" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>{plan.description}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Import Plans Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
      <DialogContent style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }} className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} /> Importar Tarifas desde JSON
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="text-sm mb-1 block" style={{ color: 'var(--text-secondary)' }}>Gimnasio destino</label>
            <Select value={importGymId || "none"} onValueChange={(v) => setImportGymId(v === "none" ? "" : v)}>
              <SelectTrigger style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-secondary)' }} data-testid="import-plan-gym-select">
                <SelectValue placeholder="Seleccionar gimnasio" />
              </SelectTrigger>
              <SelectContent style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-secondary)' }}>
                <SelectItem value="none">Seleccionar...</SelectItem>
                {gyms.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm mb-2 block" style={{ color: 'var(--text-secondary)' }}>Archivo JSON de tarifas</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={(e) => {
                const f = e.target.files[0];
                setImportFile(f);
                if (f) {
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    try {
                      const data = JSON.parse(ev.target.result);
                      setImportPreview(data.plans || []);
                    } catch { setImportPreview(null); toast.error('JSON invalido'); }
                  };
                  reader.readAsText(f);
                }
              }}
              className="hidden"
              data-testid="import-plan-file"
            />
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-zinc-500 transition-colors"
              style={{ borderColor: 'var(--border-secondary)' }}
            >
              {importFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet size={24} className="text-emerald-400" />
                  <div className="text-left">
                    <p className="font-medium">{importFile.name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{importPreview?.length || 0} tarifas encontradas</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload size={32} className="mx-auto mb-2" style={{ color: 'var(--text-muted)' }} />
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Haz clic para seleccionar archivo</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Formato: .json (generado por scraping)</p>
                </>
              )}
            </div>
          </div>

          {importPreview && importPreview.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg p-3" style={{ background: 'var(--bg-tertiary)' }}>
              <p className="text-xs font-bold mb-2" style={{ color: 'var(--text-secondary)' }}>Vista previa ({importPreview.length} tarifas):</p>
              {importPreview.map((p, i) => (
                <div key={i} className="flex justify-between text-xs py-1 border-b" style={{ borderColor: 'var(--border-primary)' }}>
                  <span>{p.name}</span>
                  <span style={{ color: 'var(--gym-primary)' }}>{p.price}€ / {p.tipo_cuota || 'mensual'}</span>
                </div>
              ))}
            </div>
          )}

          {importResult && (
            <div className={`p-4 rounded-lg border ${importResult.success ? 'bg-emerald-900/20 border-emerald-700' : 'bg-red-900/20 border-red-700'}`}>
              <p className="font-medium text-sm">{importResult.message}</p>
            </div>
          )}

          <Button 
            onClick={async () => {
              if (!importPreview || !importGymId) { toast.error('Selecciona gym y archivo'); return; }
              setImporting(true); setImportResult(null);
              try {
                const res = await importPlans({ gym_id: importGymId, plans: importPreview });
                setImportResult(res.data);
                toast.success(res.data.message);
                fetchPlans();
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Error');
              } finally { setImporting(false); }
            }}
            disabled={importing || !importPreview || !importGymId}
            className="w-full btn-gym-primary"
            data-testid="import-plans-submit"
          >
            {importing ? (
              <><Loader2 size={16} className="mr-2 animate-spin" /> Importando...</>
            ) : (
              <><Upload size={16} className="mr-2" /> Importar {importPreview?.length || 0} Tarifas</>
            )}
          </Button>
        </div>
      </DialogContent>
      </Dialog>
    </div>
  );
}
